import type { ProductPaymentMethod } from '@/services';
import { Button, Modal, WarningNotice } from '@/shared/ui';
import { zodFieldValidator } from '@/shared/utils';
import { usePaymentMethodForm } from '../hooks/use-payment-method-form.hook';
import { paymentMethodFieldsSchema } from '../schemas/payment-method.schema';
import { METHOD_STATUS_OPTIONS } from '../utils/payment-gateway-options.util';
import { BILLING_METHOD_OPTIONS } from '../utils/payment-settings-labels.util';

type PaymentMethodFormModalProps = {
  productCode: string;
  /** Ausente = adicionar uma forma nova. */
  method?: ProductPaymentMethod;
  onClose: () => void;
};

/** Adicionar ou editar uma forma do catálogo do produto. */
export function PaymentMethodFormModal({
  productCode,
  method,
  onClose,
}: PaymentMethodFormModalProps) {
  const {
    form,
    isEdit,
    isSaving,
    serverError,
    bankError,
    clearBankError,
    requiresBank,
  } = usePaymentMethodForm({
    productCode,
    method,
    onDone: onClose,
  });
  const shape = paymentMethodFieldsSchema.shape;

  return (
    <Modal
      isOpen
      onClose={onClose}
      isBusy={isSaving}
      title={
        isEdit ? `Editar forma ${method?.code}` : 'Adicionar forma de pagamento'
      }
      footer={
        <>
          <Button
            type='button'
            variant='ghost'
            size='md'
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            type='submit'
            form='payment-method-form'
            variant='primary'
            size='md'
            loading={isSaving}
          >
            Salvar forma
          </Button>
        </>
      }
    >
      <form
        id='payment-method-form'
        className='flex flex-col gap-4'
        onSubmit={event => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField
          name='code'
          validators={{ onChange: zodFieldValidator(shape.code) }}
          children={field => (
            <field.TextField
              label='Código (CodForma)'
              placeholder='10104'
              helperText={
                isEdit
                  ? 'O código não muda: ele já está gravado nas apólices.'
                  : undefined
              }
              maxLength={20}
              disabled={isEdit}
              required
            />
          )}
        />
        <form.AppField
          name='description'
          validators={{ onChange: zodFieldValidator(shape.description) }}
          children={field => (
            <field.TextField
              label='Descrição exibida ao corretor'
              placeholder='PIX'
              maxLength={100}
              required
            />
          )}
        />
        <form.AppField
          name='billingMethod'
          listeners={{ onChange: clearBankError }}
          children={field => (
            <field.SelectField
              label='Uso no gateway'
              options={BILLING_METHOD_OPTIONS}
              required
            />
          )}
        />
        {requiresBank && (
          <div className='flex flex-col gap-1'>
            <form.AppField
              name='bankNumber'
              validators={{ onChange: zodFieldValidator(shape.bankNumber) }}
              listeners={{ onChange: clearBankError }}
              children={field => (
                <field.NumberField
                  label='Código do banco (débito em conta)'
                  placeholder='341'
                  min={1}
                  max={9999}
                  required
                />
              )}
            />
            {bankError && (
              <p role='alert' className='text-sm text-alba-error-red-600'>
                {bankError}
              </p>
            )}
          </div>
        )}
        <form.AppField
          name='status'
          children={field => (
            <field.SelectField
              label='Status'
              options={METHOD_STATUS_OPTIONS}
              required
            />
          )}
        />

        <WarningNotice>
          O código precisa existir no I4Pro. A emissão reconhece boleto e débito
          pelas palavras <strong>boleto</strong> e <strong>débito</strong> na
          descrição — mantenha-as nessas formas.
        </WarningNotice>

        {serverError && (
          <p
            role='alert'
            className='rounded-lg border border-alba-error-red-400 bg-alba-error-red-400/10 p-3 text-sm text-alba-error-red-600'
          >
            {serverError}
          </p>
        )}
      </form>
    </Modal>
  );
}
